import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ShoppingBag,
  AlertCircle,
  Calendar,
  TrendingUp,
  Eye,
  Download,
  Building2,
  School,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  DollarSign,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency, formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";

import { AnimatedContainer } from "@/components/AnimatedContainer";
import { useUser } from "@/contexts/UserContext";
import { useQuery } from "@tanstack/react-query";
import { clientsApi, ordersApi, invoicingApi } from "@/lib/api";

import { FinancialSkeleton } from "./Financial/component/FinancialSkeleton";
import { CustomerPortalOverview } from "./CustomerPortalOverview";

type DashboardViewProps = Pick<ReturnType<typeof useUser>, "selectedClient" | "user" | "selectedCycleYear">;

export const DashboardView = ({ selectedClient, user, selectedCycleYear }: DashboardViewProps) => {

  // const { data: orders, isLoading } = useQuery( {
  //   queryKey: [ "orders", "all" ],
  //   queryFn: async () => {
  //     const response = await ordersApi.getOrderHistory();
  //     return response.data || [];
  //   },
  //   staleTime: 1000 * 60 * 5,
  // } );

  // const { data: quantityOrders, isLoading: isLoadingQuantityOrders } = useQuery( {
  //   queryKey: [ "quantityOrders", selectedClient?.codigo_cnx ],
  //   queryFn: async () => {
  //     if ( !selectedClient?.clientId ) return null;
  //     const response = await clientsApi.getQuantityOrders( Number( selectedClient.clientId ) );
  //     return response.data || null;
  //   },
  //   enabled: !!selectedClient,
  //   staleTime: 1000 * 60 * 5,
  // } );

  // const { data: delinquencyData, isLoading: isLoadingDelinquency } = useQuery( {
  //   queryKey: [ "delinquencyStatus", selectedClient?.cnpj?.toString().replace( /\D/g, "" ) ],
  //   queryFn: async () => {
  //     if ( !selectedClient?.cnpj ) return null;
  //     const cleanCnpj = selectedClient.cnpj.toString().replace( /\D/g, "" );
  //     const response = await invoicingApi.delinquencyStatus( cleanCnpj );
  //     // Handle both wrapped { data: DelinquencyStatus } and direct DelinquencyStatus response
  //     return ( response as any )?.data || response || null;
  //   },
  //   enabled: !!selectedClient?.cnpj,
  //   staleTime: 0,
  //   gcTime: 0,
  //   refetchOnWindowFocus: true,
  // } );

  // Pedidos do cliente ordenados por data (mais recentes primeiro)
  // const userOrders = useMemo( () => {
  //   if ( !selectedClient || !orders ) {
  //     return [];
  //   }
  //   return orders
  //     .filter( ( order ) => order.clientCode === selectedClient.codigo_cnx )
  //     .sort(
  //       ( a, b ) =>
  //         new Date( b.createdAt ).getTime() - new Date( a.createdAt ).getTime()
  //     );
  // }, [ selectedClient, orders ] );

  // const isInadimplente = useMemo( () => Number( delinquencyData?.delinquencyStatus ) === 2, [ delinquencyData ] );

  // Status badge variant helper
  const getStatusVariant = (
    status: string
  ): "default" | "secondary" | "destructive" | "outline" | "processed" => {
    switch ( status?.toLowerCase() ) {
      case "delivered":
      case "entregue":
        return "default";
      case "processed":
      case "processado":
        return "processed";
      case "processing":
      case "em processamento":
        return "secondary";
      case "cancelled":
      case "cancelado":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getStatusLabel = ( status: string ): string => {
    const labels: Record<string, string> = {
      pending: "Criado",
      created: "Criado",
      processed: "Processado",
      processing: "Em Processamento",
      delivered: "Entregue",
      cancelled: "Cancelado",
    };
    return labels[ status?.toLowerCase() ] || status;
  };

  const getInitials = ( name: string ): string => {
    return name
      .split( " " )
      .filter( Boolean )
      .map( ( n ) => n[ 0 ] )
      .join( "" )
      .substring( 0, 2 )
      .toUpperCase();
  };

  // if ( isLoadingDelinquency ) {
  //   return <FinancialSkeleton />;
  // }

  return (
    <div className="min-h-screen bg-muted/50 pb-16">
      {/* Hero Header Section - Compact */}
      <div className="bg-white border-b shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            {/* User & Client Info */}
            <div className="flex items-start gap-5">
              <div className={cn(
                "h-16 w-16 rounded-2xl flex items-center justify-center shadow-inner relative overflow-hidden transition-all duration-500",
                "bg-primary/5 text-primary"
                // isInadimplente ? "bg-orange-50 text-orange-600" : "bg-primary/5 text-primary"
              )}>
                <School className="h-8 w-8 relative z-10" />
                <div className={cn(
                  "absolute inset-0 opacity-10",
                  'bg-primary'
                  // isInadimplente ? "bg-orange-600" : "bg-primary"
                )}></div>
              </div>
              <div className="space-y-0.5">
                <div className="flex items-center gap-2.5">
                  <h1 className="text-2xl font-bold text-foreground tracking-tight">
                    Olá, {user?.name?.split( " " )[ 0 ]}
                  </h1>
                  <Badge variant="secondary" className="bg-muted text-muted-foreground font-bold text-[10px] h-5 border-none px-2 uppercase tracking-wider">
                    Cliente Ativo
                  </Badge>
                </div>
                <p className="text-muted-foreground text-base font-medium">
                  Painel de controle da <span className="text-foreground font-bold underline decoration-primary/30 decoration-2 underline-offset-4">{selectedClient?.nome_cliente || "Unidade Escolar"}</span>
                </p>
                <div className="flex items-center gap-4 pt-1.5">
                  <span className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                    <div className="h-1 w-1 rounded-full bg-muted-foreground/30"></div>
                    CNX: <span className="text-muted-foreground font-bold">{selectedClient?.codigo_cnx}</span>
                  </span>
                  <span className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                    <div className="h-1 w-1 rounded-full bg-muted-foreground/30"></div>
                    Documento: <span className="text-muted-foreground font-bold">{selectedClient?.cnpj}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Financial Health Snapshot - Refined */}
            {/* {selectedClient && (
              <div className="lg:min-w-[300px]">
                <div className={cn(
                  "p-1 rounded-3xl border transition-all duration-500 shadow-sm",
                  isInadimplente
                    ? "bg-orange-50 border-orange-400 ring-4 ring-orange-50/50"
                    : "bg-emerald-50 border-emerald-400 ring-4 ring-emerald-50/50"
                )}>
                  <div className="bg-white rounded-[1.25rem] px-5 py-3 flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-muted-foreground leading-none">Status da Unidade</p>
                      <p className={cn(
                        "text-base font-bold tracking-tight leading-none",
                        isInadimplente ? "text-orange-600" : "text-emerald-600"
                      )}>
                        {delinquencyData?.delinquencyStatusDescription || ( isInadimplente ? "Inadimplente" : "Adimplente" )}
                      </p>
                    </div>
                    <div className={cn(
                      "h-10 w-10 rounded-xl flex items-center justify-center transition-transform hover:scale-105",
                      isInadimplente ? "bg-orange-600 text-white shadow-lg shadow-orange-200" : "bg-emerald-600 text-white shadow-lg shadow-emerald-200"
                    )}>
                      {isInadimplente ? <AlertCircle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
                    </div>
                  </div>
                </div>
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="container mx-auto px-6 py-10 space-y-10">
        {selectedClient != null ? (
          <>
            {/* Delinquency Insight Card - Refined */}
            {/* isInadimplente && (
              <AnimatedContainer variant="slideUp" className="relative group">
                <div className="absolute inset-0 bg-red-600/5 blur-2xl rounded-[2.5rem] -z-10 transition-all group-hover:bg-red-600/10"></div>
                <div className="bg-white border border-red-100/80 rounded-[2rem] overflow-hidden shadow-[0_15px_40px_rgba(239,68,68,0.06)]">
                  <div className="flex flex-col lg:flex-row">
                    {/* Left: Indicator *=/}
                    <div className="bg-red-600 w-full lg:w-4 flex items-center justify-center p-4 lg:p-0">
                      <div className="lg:-rotate-90 lg:whitespace-nowrap font-bold text-white text-[10px] uppercase tracking-widest hidden lg:block opacity-50">
                        Restrição Financeira
                      </div>
                    </div>
                    {/* Right: Content *=/}
                    <div className="flex-1 p-6 lg:p-8 flex flex-col md:flex-row items-center justify-between gap-8">
                      <div className="flex items-center gap-5">
                        <div className="h-14 w-14 rounded-2xl bg-red-50 flex items-center justify-center shrink-0 border border-red-100 shadow-inner">
                          <AlertTriangle className="h-7 w-7 text-red-600" />
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-xl font-bold text-foreground tracking-tight">Detectamos pendências na sua conta</h3>
                          <p className="text-muted-foreground font-medium text-base">
                            Existem <span className="text-red-600 font-bold px-2 py-0.5 bg-red-50 rounded-lg">{quantityOrders?.overdueInstallments?.quantity || 0} faturas vencidas</span> somando <span className="text-foreground font-bold">{formatCurrency( quantityOrders?.overdueInstallments?.amount || 0 )}</span>.
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="flex items-center gap-1.5 text-[10px] font-bold text-red-500 uppercase tracking-widest bg-red-50/50 px-2 py-1 rounded-md">
                              <div className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse"></div>
                              Novos pedidos suspensos
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* <Link to="/customer-portal/financeiro">
                        <Button className="h-14 px-8 rounded-2xl bg-foreground hover:bg-black text-white font-bold text-xs uppercase tracking-[0.1em] shadow-xl transition-all hover:-translate-y-0.5 active:scale-95 flex gap-2.5">
                          Regularizar Faturas
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link> *=/}
                    </div>
                  </div>
                </div>
              </AnimatedContainer>
            )*/}

            {/* Quick Stats Grid - High Contrast Cards */}
            {/* <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              <AnimatedContainer variant="scale" delay={0.1}>
                <Card className="border shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all h-full rounded-2xl overflow-hidden bg-white">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between py-5 px-6">
                    <CardTitle className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Total de Pedidos</CardTitle>
                    <div className="h-9 w-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shadow-inner">
                      <ShoppingBag className="h-4 w-4" />
                    </div>
                  </CardHeader>
                  <CardContent className="px-6 pb-6">
                    <div className="text-3xl font-bold text-foreground tracking-tight">{quantityOrders?.totalOrders.quantity || 0}</div>
                    <p className="text-[12px] text-muted-foreground mt-2 font-bold">
                      Investimento: <span className="text-blue-600">{formatCurrency( quantityOrders?.totalOrders.amount || 0 )}</span>
                    </p>
                  </CardContent>
                </Card>
              </AnimatedContainer>

              <AnimatedContainer variant="scale" delay={0.2}>
                <Card className="border shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all h-full rounded-2xl overflow-hidden bg-white">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between py-5 px-6">
                    <CardTitle className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Parcelas a Vencer</CardTitle>
                    <div className="h-9 w-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shadow-inner">
                      <TrendingUp className="h-4 w-4" />
                    </div>
                  </CardHeader>
                  <CardContent className="px-6 pb-6">
                    <div className="text-3xl font-bold text-foreground tracking-tight">{quantityOrders?.outstandingInstallments.quantity || 0}</div>
                    <p className="text-[12px] text-muted-foreground mt-2 font-bold">
                      Em aberto: <span className="text-amber-600">{formatCurrency( quantityOrders?.outstandingInstallments.amount || 0 )}</span>
                    </p>
                  </CardContent>
                </Card>
              </AnimatedContainer>

              <AnimatedContainer variant="scale" delay={0.3}>
                <Card className={cn(
                  "border transition-all h-full rounded-2xl overflow-hidden relative",
                  isInadimplente
                    ? "bg-white border-orange-200 shadow-[0_8px_30px_rgba(234,88,12,0.1)] ring-1 ring-orange-100"
                    : "bg-white shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
                )}>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between py-5 px-6">
                    <CardTitle className={cn(
                      "text-[10px] font-bold uppercase tracking-widest",
                      isInadimplente ? "text-orange-600" : "text-muted-foreground"
                    )}>Parcelas Vencidas</CardTitle>
                    <div className={cn(
                      "h-9 w-9 rounded-xl flex items-center justify-center transition-all shadow-sm",
                      isInadimplente ? "bg-orange-600 text-white" : "bg-muted text-muted-foreground"
                    )}>
                      <AlertCircle className="h-4 w-4" />
                    </div>
                  </CardHeader>
                  <CardContent className="px-6 pb-6">
                    <div className={cn( "text-3xl font-bold tracking-tight", isInadimplente ? "text-orange-600" : "text-foreground" )}>
                      {quantityOrders?.overdueInstallments.quantity || 0}
                    </div>
                    <p className="text-[12px] text-muted-foreground mt-2 font-bold">
                      Vencido: <span className={isInadimplente ? "text-orange-600" : "text-muted-foreground"}>{formatCurrency( quantityOrders?.overdueInstallments.amount || 0 )}</span>
                    </p>
                  </CardContent>
                </Card>
              </AnimatedContainer>

              <AnimatedContainer variant="scale" delay={0.4}>
                <Card className="border shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all h-full rounded-2xl overflow-hidden bg-white">
                  <CardHeader className="pb-2 flex flex-row items-center justify-between py-5 px-6">
                    <CardTitle className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Próximo Vencimento</CardTitle>
                    <div className="h-9 w-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shadow-inner">
                      <Calendar className="h-4 w-4" />
                    </div>
                  </CardHeader>
                  <CardContent className="px-6 pb-6">
                    {quantityOrders?.nextDueDate?.date ? (
                      <>
                        <div className="text-xl font-bold text-foreground tracking-tight">
                          {formatDate( new Date( quantityOrders.nextDueDate.date ) )}
                        </div>
                        <p className="text-[12px] text-muted-foreground mt-2 font-bold uppercase tracking-tighter">
                          Valor: <span className="text-emerald-600">{formatCurrency( quantityOrders.nextDueDate.amount )}</span>
                        </p>
                      </>
                    ) : (
                      <div className="text-sm font-bold text-muted-foreground py-2 italic opacity-60">Sem pendências</div>
                    )}
                  </CardContent>
                </Card>
              </AnimatedContainer>
            </div> */}

            {/* Recent Orders Section - Refined Spacing */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* <div className="lg:col-span-2">
                <Card className="border ring-1 ring-border/50 shadow-sm rounded-2xl overflow-hidden bg-white">
                  <CardHeader className="bg-muted/40 border-b border-border/60 py-4 px-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base font-bold text-foreground flex items-center gap-2">
                          <ShoppingBag className="h-4 w-4 text-primary" />
                          Últimos Pedidos
                        </CardTitle>
                      </div>
                      <Link to="/customer-portal/pedidos">
                        <Button variant="ghost" size="sm" className="font-bold text-[10px] h-8 text-primary hover:bg-primary/5 px-3 uppercase tracking-wider">
                          Ver Histórico
                        </Button>
                      </Link>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader className="bg-muted/20">
                        <TableRow className="hover:bg-transparent border-border">
                          <TableHead className="font-bold text-[10px] uppercase tracking-widest text-muted-foreground py-3 px-6">Número</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-widest text-muted-foreground">Data</TableHead>
                          <TableHead className="font-bold text-[10px] uppercase tracking-widest text-muted-foreground">Status</TableHead>
                          <TableHead className="text-right font-bold text-[10px] uppercase tracking-widest text-muted-foreground px-6">Valor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {userOrders.length > 0 ? (
                          userOrders.slice( 0, 5 ).map( ( order ) => (
                            <TableRow key={order.id} className="hover:bg-muted/40 transition-colors border-border">
                              <TableCell className="font-bold text-foreground py-3 px-6 text-sm">
                                #{order.orderNumber}
                              </TableCell>
                              <TableCell className="font-medium text-muted-foreground text-xs">
                                {formatDate( new Date( order.createdAt ) )}
                              </TableCell>
                              <TableCell>
                                <Badge variant={getStatusVariant( order.status )} className="font-bold text-[9px] uppercase tracking-widest px-2 py-0.5 border-none">
                                  {getStatusLabel( order.status )}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-bold text-foreground text-sm px-6">
                                {formatCurrency( order.totalAmount )}
                              </TableCell>
                            </TableRow>
                          ) )
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-10 text-muted-foreground text-xs font-bold italic">
                              Nenhum pedido recente encontrado.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div> */}

              {/* Quick Access Card - Compact */}
              <div className="space-y-6">
                <Card className="border ring-1 ring-border/50 shadow-sm rounded-2xl overflow-hidden bg-white">
                  <CardHeader className="py-5 px-6">
                    <CardTitle className="text-base font-bold text-foreground">Acesso Rápido</CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-2.5 px-6 pb-6">
                    <Link to="/customer-portal/realizar-pedido">
                      <Button className={"w-full justify-between text-[12px] h-12 rounded-xl transition-all tracking-widest bg-primary hover:bg-primary/90 text-white shadow-lg"} >
                        <span className="flex items-center gap-2.5">
                          <ShoppingBag className="h-4 w-4" />
                          Realizar Novo Pedido
                        </span>
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/customer-portal/pedidos">
                      <Button variant="outline" className="w-full justify-between text-[020817B2] text-[12px] h-12 rounded-xl border-border hover:border-border transition-all tracking-widest">
                        <span className="flex items-center gap-2.5">
                          <AlertCircle className="h-4 w-4 text-muted-foreground" />
                          Pedidos Realizados
                        </span>
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/customer-portal/pedido-programado">
                      <Button variant="outline" className="w-full justify-between text-[020817B2] text-[12px] h-12 rounded-xl border-border hover:border-border transition-all tracking-widest">
                        <span className="flex items-center gap-2.5">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          Pedidos Programados
                        </span>
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/customer-portal/financeiro">
                      <Button variant="outline" className="w-full justify-between text-[020817B2] text-[12px] h-12 rounded-xl border-border hover:border-border transition-all tracking-widest">
                        <span className="flex items-center gap-2.5">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          Financeiro
                        </span>
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
              <CustomerPortalOverview
                clientCode={selectedClient.codigo_cnx}
                initialCycleYear={selectedCycleYear}
              />
            </div>
          </>
        ) : (
          <div className="py-24 text-center bg-white rounded-2xl border border-dashed border-border shadow-sm">
            <School className="h-10 w-10 text-muted-foreground/40 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-foreground tracking-tight">Unidade escolar não selecionada</h3>
            <p className="text-muted-foreground text-sm font-medium">Selecione uma unidade no topo para visualizar as métricas.</p>
          </div>
        )}
      </main>
    </div>
  );
};

const Dashboard = () => {
  const { selectedClient, user, selectedCycleYear } = useUser();
  return <DashboardView selectedClient={selectedClient} user={user} selectedCycleYear={selectedCycleYear} />;
};

export default Dashboard;
